import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useForm,
  Controller,
  useWatch,
  type Control,
  type FieldErrors,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getChannelsForRetailerFrom } from "../config/retailerConfig";
import { getUploadDescriptorHint } from "../config/retailerConfig";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { FileUploadDropzone } from "../components/upload/FileUploadDropzone";
import { UploadGuidelines } from "../components/upload/UploadGuidelines";
import { UploadStatusPanel } from "../components/upload/UploadStatusPanel";
import { useUploadConfig } from "../hooks/useUploadConfig";
import { useUploadPageWorkflow } from "../hooks/useUploadPageWorkflow";
import {
  PRODUCT_OPTIONS,
  uploadFormSchema,
  type UploadFormData,
} from "./uploadFormSchema";

const fieldLabelClass = "text-gray-900 dark:text-[#FFF8F9]";
const selectTriggerClass =
  "mt-2 w-full dark:border-[#5B5353] dark:bg-[#5B5353] dark:text-[#FFF8F9]";
const disabledSubmitClass =
  "cursor-not-allowed bg-gray-300 text-gray-500 dark:bg-[#5B5353] dark:text-[#B5A9AA]";

type UploadSelectOption = {
  id: string;
  name: string;
  description?: string;
};

type UploadSelectFieldProps = {
  control: Control<UploadFormData>;
  disabled?: boolean;
  error?: string;
  helpText?: React.ReactNode;
  label: string;
  name: "retailer" | "client" | "channel";
  options: UploadSelectOption[];
  placeholder: string;
};

const UploadSelectField: React.FC<UploadSelectFieldProps> = ({
  control,
  disabled = false,
  error,
  helpText,
  label,
  name,
  options,
  placeholder,
}) => (
  <div>
    <Label htmlFor={name} className={fieldLabelClass}>
      {label} *
    </Label>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Select
          onValueChange={field.onChange}
          value={field.value}
          disabled={disabled}
        >
          <SelectTrigger id={name} className={selectTriggerClass}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
    {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
    {helpText}
  </div>
);

type UploadMetadataFieldsProps = {
  availableChannels: UploadSelectOption[];
  clientOptions: UploadSelectOption[];
  control: Control<UploadFormData>;
  errors?: FieldErrors<UploadFormData>;
  productKey: string;
  retailerOptions: UploadSelectOption[];
  selectedChannel?: string;
  selectedProduct?: string;
  selectedRetailer?: string;
  showErrors?: boolean;
  showProductSelector?: boolean;
  descriptorHelpText?: React.ReactNode;
};

const UploadMetadataFields: React.FC<UploadMetadataFieldsProps> = ({
  availableChannels,
  clientOptions,
  control,
  descriptorHelpText,
  errors,
  productKey,
  retailerOptions,
  selectedChannel,
  selectedProduct,
  selectedRetailer,
  showErrors = false,
  showProductSelector = false,
}) => {
  const channelDescription = availableChannels.find(
    (channel) => channel.id === selectedChannel,
  )?.description;

  return (
    <div className="mb-8 space-y-6">
      <UploadSelectField
        control={control}
        error={showErrors ? errors?.retailer?.message : undefined}
        label="Retailer"
        name="retailer"
        options={retailerOptions}
        placeholder="Select retailer"
      />

      <UploadSelectField
        control={control}
        error={showErrors ? errors?.client?.message : undefined}
        label="Client"
        name="client"
        options={clientOptions}
        placeholder="Select client"
      />

      <UploadSelectField
        control={control}
        disabled={!selectedRetailer || availableChannels.length === 0}
        error={showErrors ? errors?.channel?.message : undefined}
        helpText={
          descriptorHelpText ??
          (selectedChannel && channelDescription ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-[#B5A9AA]">
              {channelDescription}
            </p>
          ) : null)
        }
        label="Channel"
        name="channel"
        options={availableChannels}
        placeholder="Select channel"
      />

      {showProductSelector && (
        <div>
          <Label htmlFor="product" className={fieldLabelClass}>
            Product (Optional)
          </Label>
          <Controller
            name="product"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value || "all"}>
                <SelectTrigger id="product" className={selectTriggerClass}>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCT_OPTIONS[productKey]?.map((product) => (
                    <SelectItem key={product.value} value={product.value}>
                      {product.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {selectedProduct === "gyoza" && (
            <p className="mt-1 flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
              <span className="inline-block h-2 w-2 rounded-full bg-blue-600 dark:bg-blue-400"></span>
              Enhanced report format with product-level breakdown, add-to-cart metrics,
              in-store sales, and lift analysis
            </p>
          )}
        </div>
      )}
    </div>
  );
};

type ComparisonFileInputProps = {
  accept: string;
  file: File | null;
  label: string;
  onChange: (file: File | null) => void;
  placeholder: string;
  tone: "emerald" | "gray";
};

const ComparisonFileInput: React.FC<ComparisonFileInputProps> = ({
  accept,
  file,
  label,
  onChange,
  placeholder,
  tone,
}) => {
  const toneClasses =
    tone === "emerald"
      ? "border-emerald-300 bg-emerald-50/50 hover:border-emerald-400 hover:bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/20 dark:hover:border-emerald-600"
      : "border-gray-300 bg-gray-50/50 hover:border-gray-400 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800/20 dark:hover:border-gray-500";
  const fileNameClasses =
    tone === "emerald"
      ? "font-medium text-emerald-700 dark:text-emerald-300"
      : "font-medium text-gray-700 dark:text-gray-300";

  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#FFF1F3]">
        {label} *
      </label>
      <div className="flex items-center gap-3">
        <label
          className={`flex-1 cursor-pointer rounded-lg border-2 border-dashed px-4 py-3 text-center text-sm transition-colors ${toneClasses}`}
        >
          <input
            type="file"
            accept={accept}
            className="hidden"
            onChange={(event) => onChange(event.target.files?.[0] ?? null)}
          />
          {file ? (
            <span className={fileNameClasses}>{file.name}</span>
          ) : (
            <span className="text-gray-500 dark:text-gray-400">{placeholder}</span>
          )}
        </label>
        {file && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-gray-400 hover:text-red-500"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

type UploadSubmitButtonProps = {
  activeClassName: string;
  disabled: boolean;
  idleLabel: string;
  isUploading: boolean;
  uploadingLabel: string;
};

const UploadSubmitButton: React.FC<UploadSubmitButtonProps> = ({
  activeClassName,
  disabled,
  idleLabel,
  isUploading,
  uploadingLabel,
}) => (
  <button
    type="submit"
    disabled={disabled}
    className={`w-full rounded-lg px-4 py-3 font-medium transition-all ${
      disabled ? disabledSubmitClass : activeClassName
    }`}
  >
    {isUploading ? uploadingLabel : idleLabel}
  </button>
);

type UploadFormFrameProps = {
  activeClassName: string;
  children: React.ReactNode;
  disabled: boolean;
  idleLabel: string;
  isUploading: boolean;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  progressTone: "blue" | "purple";
  uploadError: string | null;
  uploadProgress: number;
  uploadingLabel: string;
};

const UploadFormFrame: React.FC<UploadFormFrameProps> = ({
  activeClassName,
  children,
  disabled,
  idleLabel,
  isUploading,
  onSubmit,
  progressTone,
  uploadError,
  uploadProgress,
  uploadingLabel,
}) => (
  <form onSubmit={onSubmit}>
    {children}
    <UploadStatusPanel
      error={uploadError}
      isUploading={isUploading}
      progress={uploadProgress}
      progressTone={progressTone}
    />
    <UploadSubmitButton
      activeClassName={activeClassName}
      disabled={disabled}
      idleLabel={idleLabel}
      isUploading={isUploading}
      uploadingLabel={uploadingLabel}
    />
  </form>
);

export const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    control,
    handleSubmit: handleFormSubmit,
    setValue,
    formState: { errors },
  } = useForm<UploadFormData>({
    resolver: zodResolver(uploadFormSchema),
    defaultValues: {
      retailer: "walmart",
      client: "ajinomoto",
      channel: "search",
    },
  });

  const selectedRetailer = useWatch({ control, name: "retailer" });
  const selectedClient = useWatch({ control, name: "client" });
  const selectedChannel = useWatch({ control, name: "channel" });
  const selectedProduct = useWatch({ control, name: "product" });
  const { clients, retailers } = useUploadConfig();

  const productKey = `${selectedClient}-${selectedChannel}`;
  const showProductSelector = PRODUCT_OPTIONS[productKey] !== undefined;
  const availableChannels = useMemo(
    () => getChannelsForRetailerFrom(retailers, selectedRetailer || ""),
    [retailers, selectedRetailer],
  );
  const {
    activateComparisonMode,
    activateLinearMode,
    activateStandardMode,
    comparisonCurrentFile,
    comparisonPriorFile,
    dragActive,
    handleDrag,
    handleDrop,
    handleFileSelect,
    handleReset,
    hasValidFileSelection,
    isComparisonMode,
    isHessMode,
    isLinearMode,
    isStreamingMode,
    selectedDescriptor,
    isUploading,
    onSubmit,
    selectedFile,
    selectedFiles,
    setComparisonFile,
    uploadError,
    uploadProgress,
  } = useUploadPageWorkflow({
    navigate,
    retailers,
    selectedChannel,
    selectedRetailer,
  });

  useEffect(() => {
    if (retailers.length === 0) {
      return;
    }

    const currentRetailer = retailers.find(
      (retailer) => retailer.id === selectedRetailer,
    );

    if (!currentRetailer) {
      const firstRetailer = retailers[0];
      setValue("retailer", firstRetailer.id);
      setValue("channel", firstRetailer.channels[0]?.id ?? "");
      return;
    }

    if (availableChannels.length > 0) {
      const isChannelValid = availableChannels.some(
        (channel) => channel.id === selectedChannel,
      );
      if (!isChannelValid) {
        setValue("channel", availableChannels[0].id);
      }
    }
  }, [availableChannels, retailers, selectedChannel, selectedRetailer, setValue]);

  useEffect(() => {
    if (
      clients.length > 0 &&
      !clients.some((client) => client.id === selectedClient)
    ) {
      setValue("client", clients[0].id);
    }
  }, [clients, selectedClient, setValue]);

  const descriptorHint = getUploadDescriptorHint(selectedDescriptor);
  const acceptedFileTypes = selectedDescriptor.fileFormats.join(",");
  const selectedDescriptorText = isComparisonMode
    ? selectedDescriptor.name
    : selectedDescriptor.description;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-12 dark:from-[#2C2525] dark:to-[#2C2525] sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-4xl font-bold text-gray-900 dark:text-[#FFF8F9]">
            Campaign Report Upload
          </h1>
          <p className="text-lg text-gray-600 dark:text-[#B5A9AA]">
            Upload campaign performance reports from retailers for analysis
          </p>
        </div>

        <div className="mb-6 flex justify-center">
          <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 dark:border-[#5B5353] dark:bg-[#403838]">
            <button
              type="button"
              onClick={activateStandardMode}
              className={`rounded-md px-6 py-2 text-sm font-medium transition-all ${
                !isLinearMode && !isComparisonMode
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-gray-700 hover:bg-gray-50 dark:text-[#FFF1F3] dark:hover:bg-[#5B5353]"
              }`}
            >
              Standard Reports
            </button>
            <button
              type="button"
              onClick={activateComparisonMode}
              className={`rounded-md px-6 py-2 text-sm font-medium transition-all ${
                isComparisonMode
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "text-gray-700 hover:bg-gray-50 dark:text-[#FFF1F3] dark:hover:bg-[#5B5353]"
              }`}
            >
              Compare Periods
            </button>
            <button
              type="button"
              onClick={activateLinearMode}
              className={`rounded-md px-6 py-2 text-sm font-medium transition-all ${
                isLinearMode
                  ? "bg-purple-600 text-white shadow-sm"
                  : "text-gray-700 hover:bg-gray-50 dark:text-[#FFF1F3] dark:hover:bg-[#5B5353]"
              }`}
            >
              Linear TV
            </button>
          </div>
        </div>

        <div className="rounded-lg bg-white p-8 shadow-xl dark:bg-[#403838]">
          {isComparisonMode ? (
            <UploadFormFrame
              activeClassName="bg-emerald-600 text-white shadow-lg hover:bg-emerald-700 hover:shadow-xl"
              disabled={!hasValidFileSelection || isUploading}
              idleLabel="Compare Reports"
              isUploading={isUploading}
              onSubmit={handleFormSubmit(onSubmit)}
              progressTone="blue"
              uploadError={uploadError}
              uploadProgress={uploadProgress}
              uploadingLabel="Comparing..."
            >
              <div className="mb-6">
                <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-[#FFF8F9]">
                  Period-over-Period Comparison
                </h3>
                <p className="mb-4 text-sm text-gray-600 dark:text-[#B5A9AA]">
                  Upload two search reports from different time periods to see metric deltas
                </p>
              </div>

              <UploadMetadataFields
                availableChannels={availableChannels}
                clientOptions={clients}
                control={control}
                descriptorHelpText={
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    {selectedDescriptorText}
                  </p>
                }
                productKey={productKey}
                retailerOptions={retailers}
                selectedChannel={selectedChannel}
                selectedRetailer={selectedRetailer}
              />

              <div className="mb-6 space-y-4">
                <ComparisonFileInput
                  accept={acceptedFileTypes}
                  file={comparisonCurrentFile}
                  label="Current Period Report"
                  onChange={(file) => setComparisonFile("current", file)}
                  placeholder="Choose current period file..."
                  tone="emerald"
                />

                <ComparisonFileInput
                  accept={acceptedFileTypes}
                  file={comparisonPriorFile}
                  label="Prior Period Report"
                  onChange={(file) => setComparisonFile("prior", file)}
                  placeholder="Choose prior period file..."
                  tone="gray"
                />
              </div>
            </UploadFormFrame>
          ) : !isLinearMode ? (
            <UploadFormFrame
              activeClassName="bg-blue-600 text-white shadow-lg hover:bg-blue-700 hover:shadow-xl"
              disabled={!hasValidFileSelection || isUploading}
              idleLabel="Upload and Analyze"
              isUploading={isUploading}
              onSubmit={handleFormSubmit(onSubmit)}
              progressTone="blue"
              uploadError={uploadError}
              uploadProgress={uploadProgress}
              uploadingLabel="Processing..."
            >
              <UploadMetadataFields
                availableChannels={availableChannels}
                clientOptions={clients}
                control={control}
                errors={errors}
                productKey={productKey}
                retailerOptions={retailers}
                selectedChannel={selectedChannel}
                selectedProduct={selectedProduct}
                selectedRetailer={selectedRetailer}
                showErrors={true}
                showProductSelector={showProductSelector}
              />

              <div className="mb-6">
                <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-[#FFF1F3]">
                  Upload Report File *
                </label>

                <FileUploadDropzone
                  accept={acceptedFileTypes}
                  dragActive={dragActive}
                  emptyStateLabel={
                    <>
                      <span className="font-medium text-blue-600 hover:text-blue-700 dark:text-[#FFF1F3] dark:hover:text-[#FFF8F9]">
                        Choose a file
                      </span>
                      <span> or drag and drop</span>
                    </>
                  }
                  helperText={
                    <>
                      <strong>Required: {descriptorHint}</strong> - {" "}
                      {selectedDescriptor.description || "Upload report file(s)"} (up to 50MB each)
                    </>
                  }
                  inputId="file-upload"
                  isUploading={isUploading}
                  multiple={isStreamingMode || isHessMode}
                  onChange={handleFileSelect}
                  onDrag={handleDrag}
                  onDrop={handleDrop}
                  onReset={handleReset}
                  resetLabel={`Change file${isStreamingMode || isHessMode ? "s" : ""}`}
                  selectedFile={selectedFile}
                  selectedFiles={selectedFiles}
                  selectedMode={isStreamingMode || isHessMode ? "list" : "single"}
                  tone="blue"
                />
              </div>
            </UploadFormFrame>
          ) : (
            <UploadFormFrame
              activeClassName="bg-purple-600 text-white shadow-lg hover:bg-purple-700 hover:shadow-xl"
              disabled={!hasValidFileSelection || isUploading}
              idleLabel="Upload Linear TV Reports"
              isUploading={isUploading}
              onSubmit={handleFormSubmit(onSubmit)}
              progressTone="purple"
              uploadError={uploadError}
              uploadProgress={uploadProgress}
              uploadingLabel="Processing..."
            >
              <div className="mb-6">
                <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-[#FFF8F9]">
                  Linear TV Multi-Channel Upload
                </h3>
                <p className="mb-4 text-sm text-gray-600 dark:text-[#B5A9AA]">
                  {selectedDescriptor.description}
                </p>

                <FileUploadDropzone
                  accept={acceptedFileTypes}
                  dragActive={dragActive}
                  emptyStateLabel={
                    <>
                      <span className="font-medium text-purple-600 hover:text-purple-700 dark:text-[#FFF1F3] dark:hover:text-[#FFF8F9]">
                        Choose files
                      </span>
                      <span> or drag and drop</span>
                    </>
                  }
                  helperText={
                    <>
                      <strong>Required: {descriptorHint}</strong> -{" "}
                      {selectedDescriptor.description || "Upload one file per Linear TV channel"} (up to
                      50MB each)
                    </>
                  }
                  inputId="linear-upload"
                  isUploading={isUploading}
                  multiple={true}
                  onChange={handleFileSelect}
                  onDrag={handleDrag}
                  onDrop={handleDrop}
                  onReset={handleReset}
                  resetLabel="Change files"
                  selectedFiles={selectedFiles}
                  selectedMode="count-list"
                  tone="purple"
                />
              </div>
            </UploadFormFrame>
          )}
        </div>
        <UploadGuidelines />
      </div>
    </div>
  );
};
